import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getMyCommissionBalance, getMyPayouts, requestMyPayout } from "../../../redux/actions/profile";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";

const STATUS_COLOR = { pending: "#f4a261", approved: "#4fb0c6", paid: "#2ec4b6", rejected: "#e63946" };

export default function CommissionWallet() {
  const dispatch = useDispatch();
  const balance = useSelector((s) => s.profile.agentCommissionBalance);
  const payouts = useSelector((s) => s.profile.agentPayouts);

  const [showRequest, setShowRequest] = useState(false);
  const [form, setForm] = useState({
    requestedAmount: "",
    accountName: "",
    accountNumber: "",
    bankName: "",
    bankCode: "",
    note: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    dispatch(getMyCommissionBalance());
    dispatch(getMyPayouts());
  }, [dispatch]);

  const handleRequest = async () => {
    if (!form.requestedAmount || !form.accountNumber || !form.bankName) {
      return setMsg({ type: "error", text: "Amount, account number and bank name are required" });
    }
    setSubmitting(true);
    setMsg(null);
    const res = await dispatch(requestMyPayout({
      requestedAmount: Number(form.requestedAmount),
      bankDetails: {
        accountName: form.accountName,
        accountNumber: form.accountNumber,
        bankName: form.bankName,
        bankCode: form.bankCode
      },
      note: form.note
    }));
    setSubmitting(false);
    if (res?.success) {
      setMsg({ type: "success", text: "Withdrawal request submitted! You will be notified when it is processed." });
      setForm({ requestedAmount: "", accountName: "", accountNumber: "", bankName: "", bankCode: "", note: "" });
      dispatch(getMyPayouts());
      dispatch(getMyCommissionBalance());
    } else {
      setMsg({ type: "error", text: res?.message || "Request failed" });
    }
  };

  const fmtMoney = (n) => n != null ? `₦${Number(n).toLocaleString("en-NG", { minimumFractionDigits: 2 })}` : "—";
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  const hasPending = (payouts || []).some((p) => p.status === "pending");

  return (
    <div style={S.root}>
      <div style={S.header}>
        <i className="bi bi-wallet2" style={{ color: PRIMARY, fontSize: 20, marginRight: 10 }} />
        <span style={S.title}>Commission Wallet</span>
      </div>

      {/* Balance cards */}
      {balance && (
        <div style={S.balanceGrid}>
          <div style={{ ...S.balCard, borderTop: `3px solid ${PRIMARY}` }}>
            <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>Total Earned</div>
            <div style={S.balAmount}>{fmtMoney(balance.totalEarned)}</div>
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>{balance.totalMerchantsReferred} merchants referred</div>
          </div>
          <div style={{ ...S.balCard, borderTop: "3px solid #e63946" }}>
            <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>Withdrawn</div>
            <div style={{ ...S.balAmount, color: "#e63946" }}>{fmtMoney(balance.totalWithdrawn)}</div>
          </div>
          <div style={{ ...S.balCard, borderTop: "3px solid #2ec4b6" }}>
            <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>Available</div>
            <div style={{ ...S.balAmount, color: "#2ec4b6" }}>{fmtMoney(balance.availableBalance)}</div>
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>Ready to withdraw</div>
          </div>
          <div style={{ ...S.balCard, borderTop: "3px solid #9b5de5" }}>
            <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>Commission Rate</div>
            <div style={{ ...S.balAmount, color: "#9b5de5" }}>{balance.currentCommissionRate}</div>
            {balance.nextTierAt && (
              <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>
                {balance.nextTierAt - balance.totalMerchantsReferred} more merchants to next tier
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tier info */}
      <div style={S.tierCard}>
        <i className="bi bi-graph-up-arrow" style={{ color: PRIMARY, marginRight: 8 }} />
        <span style={{ fontSize: 13, color: "#555", fontWeight: 500 }}>Commission Tiers:</span>
        <div style={S.tiers}>
          {[
            { range: "1–99 merchants", rate: "10%", current: balance?.totalMerchantsReferred < 100 },
            { range: "100–999 merchants", rate: "12%", current: balance?.totalMerchantsReferred >= 100 && balance?.totalMerchantsReferred < 1000 },
            { range: "1,000+ merchants", rate: "15%", current: balance?.totalMerchantsReferred >= 1000 },
          ].map((t) => (
            <div key={t.range} style={{ ...S.tierPill, background: t.current ? PRIMARY + "18" : "#f5f5f5", color: t.current ? "#5a8a00" : "#888", border: t.current ? `1px solid ${PRIMARY}` : "1px solid #e0e0e0", fontWeight: t.current ? 700 : 400 }}>
              {t.rate} · {t.range}{t.current ? " ✓ (current)" : ""}
            </div>
          ))}
        </div>
      </div>

      {/* Withdraw button */}
      <div style={{ marginBottom: 24 }}>
        {hasPending ? (
          <div style={S.pendingNotice}>
            <i className="bi bi-clock-fill" style={{ marginRight: 8, color: "#f4a261" }} />
            You have a pending withdrawal request. Please wait for it to be processed before requesting again.
          </div>
        ) : (
          <button style={S.withdrawBtn} onClick={() => { setShowRequest(!showRequest); setMsg(null); }}>
            <i className="bi bi-bank" style={{ marginRight: 8 }} />
            {showRequest ? "Cancel" : "Request Withdrawal"}
          </button>
        )}
      </div>

      {/* Withdrawal form */}
      {showRequest && !hasPending && (
        <div style={S.requestCard}>
          <div style={{ fontSize: 15, fontWeight: 700, color: BG, marginBottom: 16 }}>Withdrawal Request</div>
          <div style={S.fieldRow}>
            <div style={S.field}>
              <label style={S.label}>Amount (₦) *</label>
              <input style={S.input} type="number" min="1" placeholder="e.g. 5000" value={form.requestedAmount} onChange={(e) => setForm({ ...form, requestedAmount: e.target.value })} />
              <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>Available: {fmtMoney(balance?.availableBalance)}</div>
            </div>
            <div style={S.field}>
              <label style={S.label}>Account Name *</label>
              <input style={S.input} placeholder="As it appears on your bank" value={form.accountName} onChange={(e) => setForm({ ...form, accountName: e.target.value })} />
            </div>
          </div>
          <div style={S.fieldRow}>
            <div style={S.field}>
              <label style={S.label}>Account Number *</label>
              <input style={S.input} placeholder="10-digit number" value={form.accountNumber} onChange={(e) => setForm({ ...form, accountNumber: e.target.value })} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Bank Name *</label>
              <input style={S.input} placeholder="e.g. GTBank" value={form.bankName} onChange={(e) => setForm({ ...form, bankName: e.target.value })} />
            </div>
          </div>
          <label style={S.label}>Note (optional)</label>
          <input style={{ ...S.input, width: "100%", boxSizing: "border-box" }} placeholder="Any message to admin…" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />

          {msg && (
            <div style={{ borderRadius: 8, padding: "10px 14px", fontSize: 13, marginTop: 12, background: msg.type === "success" ? "rgba(46,196,182,0.1)" : "rgba(230,57,70,0.08)", color: msg.type === "success" ? "#2ec4b6" : "#e63946", border: `1px solid ${msg.type === "success" ? "#2ec4b6" : "#e63946"}` }}>
              {msg.text}
            </div>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <button style={S.withdrawBtn} onClick={handleRequest} disabled={submitting}>{submitting ? "Submitting…" : "Submit Request"}</button>
            <button style={S.btnSecondary} onClick={() => setShowRequest(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Commission history (last 20) */}
      {balance?.commissionHistory?.length > 0 && (
        <div style={S.section}>
          <div style={S.sectionLabel}>Recent Commission Earnings</div>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  {["Merchant", "Plan", "Type", "Rate", "Earned", "Date"].map((h) => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {balance.commissionHistory.map((c, i) => (
                  <tr key={i} style={S.tr}>
                    <td style={S.td}>{c.merchantEmail || "—"}</td>
                    <td style={S.td}>{c.plan || "—"}</td>
                    <td style={S.td}>
                      <span style={{ background: c.isRenewal ? "#4fb0c618" : PRIMARY + "18", color: c.isRenewal ? "#4fb0c6" : "#5a8a00", borderRadius: 20, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
                        {c.isRenewal ? "Renewal" : "New"}
                      </span>
                    </td>
                    <td style={S.td}>{c.rate ? `${(c.rate * 100).toFixed(0)}%` : "—"}</td>
                    <td style={{ ...S.td, fontWeight: 600, color: "#2ec4b6" }}>{fmtMoney(c.amount)}</td>
                    <td style={{ ...S.td, color: "#888" }}>{fmtDate(c.date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Payout history */}
      {payouts?.length > 0 && (
        <div style={S.section}>
          <div style={S.sectionLabel}>Withdrawal History</div>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  {["Requested", "Bank", "Status", "Date"].map((h) => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payouts.map((p, i) => (
                  <tr key={i} style={S.tr}>
                    <td style={{ ...S.td, fontWeight: 600, color: BG }}>{fmtMoney(p.requestedAmount)}</td>
                    <td style={S.td}>
                      <div style={{ fontSize: 12 }}>{p.bankDetails?.accountName || "—"}</div>
                      <div style={{ fontSize: 11, color: "#888" }}>{p.bankDetails?.bankName} · {p.bankDetails?.accountNumber}</div>
                    </td>
                    <td style={S.td}>
                      <span style={{ background: (STATUS_COLOR[p.status] || "#888") + "18", color: STATUS_COLOR[p.status] || "#888", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>
                        {p.status}
                      </span>
                      {p.rejectionReason && <div style={{ fontSize: 11, color: "#e63946", marginTop: 2 }}>{p.rejectionReason}</div>}
                    </td>
                    <td style={{ ...S.td, color: "#888" }}>{fmtDate(p.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  root: { padding: "24px 0", color: "#333" },
  header: { display: "flex", alignItems: "center", marginBottom: 20 },
  title: { fontSize: 18, fontWeight: 800, color: BG },
  balanceGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 14, marginBottom: 20 },
  balCard: { background: "#fff", borderRadius: 10, padding: "16px 16px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" },
  balAmount: { fontSize: 20, fontWeight: 800, color: BG, marginTop: 6, lineHeight: 1.2 },
  tierCard: { background: "#fff", borderRadius: 10, padding: "14px 18px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", marginBottom: 20, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" },
  tiers: { display: "flex", gap: 8, flexWrap: "wrap" },
  tierPill: { borderRadius: 20, padding: "4px 12px", fontSize: 12 },
  withdrawBtn: { background: BG, border: "none", borderRadius: 8, color: "#fff", fontWeight: 600, padding: "10px 22px", cursor: "pointer", fontSize: 14, display: "inline-flex", alignItems: "center" },
  btnSecondary: { background: "#f5f5f5", border: "1.5px solid #e0e0e0", borderRadius: 8, color: "#666", fontWeight: 500, padding: "10px 18px", cursor: "pointer", fontSize: 13 },
  pendingNotice: { background: "rgba(244,162,97,0.1)", border: "1px solid #f4a261", borderRadius: 8, padding: "12px 16px", fontSize: 13, color: "#c47700", display: "flex", alignItems: "center" },
  requestCard: { background: "#fff", borderRadius: 10, padding: "20px 24px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", marginBottom: 24 },
  fieldRow: { display: "flex", gap: 12 },
  field: { flex: 1 },
  label: { display: "block", color: "#555", fontSize: 13, fontWeight: 600, marginBottom: 6, marginTop: 12 },
  input: { border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "9px 12px", fontSize: 13, color: "#333", background: "#fafafa", width: "100%", boxSizing: "border-box", outline: "none" },
  section: { marginBottom: 24 },
  sectionLabel: { fontSize: 12, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 },
  tableWrap: { background: "#fff", borderRadius: 10, overflowX: "auto", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", WebkitOverflowScrolling: "touch" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 500 },
  th: { background: BG, color: "#fff", padding: "10px 14px", textAlign: "left", fontSize: 12, fontWeight: 600 },
  tr: { borderBottom: "1px solid #f5f5f5" },
  td: { padding: "10px 14px", fontSize: 13, color: "#555", verticalAlign: "middle" },
};
